/**
 * Hookah reservation success page.
 *
 * Entered from the confirmation flow (?code=HK-XXXX&token=...) or from
 * the email CTA (/hookah/r/:token — same page but token comes from the
 * URL param instead of the query string).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import './HookahReservation.css';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    });
  } catch {
    return iso;
  }
};
const fmtTime = (hhmm) => {
  if (!hhmm) return '';
  try {
    return new Date(`2000-01-01T${hhmm}`).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch {
    return hhmm;
  }
};

const statusLabel = {
  PENDING: 'Confirmed',
  CHECKED_IN: 'Checked in',
  NO_SHOW: 'Missed',
  CANCELLED: 'Cancelled',
};

const HookahSuccess = () => {
  const navigate = useNavigate();
  const { token: tokenParam } = useParams();
  const [searchParams] = useSearchParams();
  const tokenFromQs = searchParams.get('token') || searchParams.get('code') || '';
  const lookupToken = tokenParam || tokenFromQs;

  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!lookupToken) {
      setLoading(false);
      setError('Missing reservation code.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.hookahLookupReservation(lookupToken);
        if (!cancelled) {
          if (resp?.success) setReservation(resp.data);
          else setError(resp?.error || 'Reservation not found.');
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Reservation not found.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lookupToken]);

  const qrSrc = useMemo(() => {
    if (!reservation) return '';
    const payload = `HOOKAH:${reservation.qr_token}:${reservation.reservation_code}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=400x400&margin=0&ecc=M&data=${encodeURIComponent(payload)}`;
  }, [reservation]);

  return (
    <div className="hk-root">
      <Helmet>
        <title>Your Hookah Reservation · OC MENA Festival</title>
      </Helmet>
      <div className="hk-shell">
        <div className="hk-card">
          {loading && <div>Loading your reservation…</div>}

          {!loading && error && (
            <div>
              <div className="hk-alert">{error}</div>
              <div className="hk-actions">
                <button className="hk-btn hk-btn-primary" onClick={() => navigate('/hookah-lounge')}>
                  Make a new reservation
                </button>
              </div>
            </div>
          )}

          {!loading && reservation && (
            <div className="hk-success">
              <div className="hk-success-check">✓</div>
              <h2>{reservation.status === 'CHECKED_IN' ? 'Welcome in!' : 'You\u2019re all set'}</h2>
              <p>
                {reservation.status === 'CHECKED_IN'
                  ? 'Our staff has seated your party — enjoy the lounge.'
                  : 'Show this QR code to our staff at the hookah lounge. Reservations are fixed to a 2-hour session.'}
              </p>

              <div className="hk-qr-card">
                <img src={qrSrc} alt="Reservation QR code" />
              </div>

              <div className="hk-reservation-code">{reservation.reservation_code}</div>
              <div className="hk-reservation-sub">Reservation code</div>

              <dl className="hk-detail-grid">
                <div className="hk-detail-cell">
                  <dt>Status</dt>
                  <dd>{statusLabel[reservation.status] || reservation.status}</dd>
                </div>
                <div className="hk-detail-cell">
                  <dt>Day</dt>
                  <dd>{fmtDate(reservation.event_date)}</dd>
                </div>
                <div className="hk-detail-cell">
                  <dt>Slot</dt>
                  <dd>{fmtTime(reservation.start_time)}</dd>
                </div>
                <div className="hk-detail-cell">
                  <dt>Session</dt>
                  <dd>2-hour reservation window</dd>
                </div>
                <div className="hk-detail-cell">
                  <dt>Party</dt>
                  <dd>{reservation.party_size} {reservation.party_size === 1 ? 'guest' : 'guests'}</dd>
                </div>
                <div className="hk-detail-cell">
                  <dt>Name</dt>
                  <dd>{reservation.customer_name}</dd>
                </div>
              </dl>

              <p className="hk-footer-note">
                Keep this page handy — the QR code is all our staff need.
                {reservation.customer_email && (
                  <> A copy has been emailed to <strong>{reservation.customer_email}</strong>.</>
                )}
              </p>

              <div className="hk-actions" style={{ justifyContent: 'center' }}>
                <button className="hk-btn hk-btn-ghost" onClick={() => navigate('/hookah-lounge')}>
                  Book another
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HookahSuccess;
